/**
 * Обращения к API.
 *
 * Сервер не отдаёт текстов для человека: в ошибке приходит стабильный `code`, строку на языке
 * интерфейса подбирает словарь (`i18n`). `message` идёт только в консоль разработчика.
 *
 * Ключи хранилища свои: на демо-стенде рядом открыты админка, склад и курьер,
 * общий токен увёл бы клиента в чужую сессию.
 */

const BUILT_IN_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3100';
const TOKEN_KEY = 'trotty10.client.token';
const LANG_KEY = 'trotty10.client.lang';
const BASE_URL_KEY = 'trotty10.client.api-base-url';

/** Адрес, который сервер последний раз назвал для веб-клиента; без него — вшитый в сборку. */
let BASE_URL: string = localStorage.getItem(BASE_URL_KEY) ?? BUILT_IN_URL;

/**
 * Спрашивает вшитый адрес, с каким API работать (`GET /v1/app/backend?app=client-web`, задаётся в
 * админке). Без ответа за три секунды остаётся последний известный адрес. Токен принадлежит
 * прежнему API, поэтому при смене адреса он сбрасывается.
 */
export async function resolveBackend(): Promise<void> {
  let answer: { apiBaseUrl?: string | null };
  try {
    const response = await fetch(`${BUILT_IN_URL}/v1/app/backend?app=client-web`, { signal: AbortSignal.timeout(3_000) });
    if (!response.ok) return;
    answer = (await response.json()) as { apiBaseUrl?: string | null };
  } catch {
    return;
  }
  const next = answer.apiBaseUrl || BUILT_IN_URL;
  if (next === BASE_URL) return;
  BASE_URL = next;
  if (answer.apiBaseUrl) localStorage.setItem(BASE_URL_KEY, answer.apiBaseUrl);
  else localStorage.removeItem(BASE_URL_KEY);
  writeToken(null);
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details && typeof details === 'object' ? (details as Record<string, unknown>) : {};
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  static offline(cause?: unknown): ApiError {
    return new ApiError(0, 'offline', cause instanceof Error ? cause.message : 'offline');
  }
}

/** Ошибка любого происхождения как `ApiError`: экраны показывают текст по коду. */
export function asApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  return new ApiError(0, 'malformed_response', error instanceof Error ? error.message : String(error));
}

type SessionLostHandler = () => void;
const sessionLostHandlers = new Set<SessionLostHandler>();

/** Сервер отверг живой токен (401): сессия отозвана, интерфейс возвращается в гостя. */
export function onSessionLost(handler: SessionLostHandler): () => void {
  sessionLostHandlers.add(handler);
  return () => sessionLostHandlers.delete(handler);
}

export function readToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function writeToken(token: string | null): void {
  if (token === null) localStorage.removeItem(TOKEN_KEY);
  else localStorage.setItem(TOKEN_KEY, token);
}

export function readLang(): string | null {
  return localStorage.getItem(LANG_KEY);
}

export function writeLang(lang: string): void {
  localStorage.setItem(LANG_KEY, lang);
}

export type Query = Record<string, string | number | boolean | null | undefined>;

export function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
};

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = readToken();
  const headers: Record<string, string> = { accept: 'application/json' };

  // FormData сама задаёт content-type с boundary, поэтому свой заголовок ставить нельзя.
  const multipart = options.body instanceof FormData;
  const body: BodyInit | undefined =
    options.body === undefined ? undefined : multipart ? (options.body as FormData) : JSON.stringify(options.body);

  if (token) headers.authorization = `Bearer ${token}`;
  if (options.body !== undefined && !multipart) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${withQuery(path, options.query)}`, {
      method: options.method ?? 'GET',
      headers,
      ...(body === undefined ? {} : { body }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw ApiError.offline(error);
  }

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    if (response.status === 401 && token) {
      writeToken(null);
      for (const handler of sessionLostHandlers) handler();
    }
    throw new ApiError(response.status, error?.code ?? 'internal', error?.message ?? `HTTP ${response.status}`, error?.details);
  }

  if (payload === null) throw new ApiError(response.status, 'malformed_response', 'Empty response body');
  return payload as T;
}

export type StreamFrame = { event: string; data: string; id: string | null };

/**
 * Поток server-sent events. `EventSource` не умеет заголовок Authorization, поэтому ответ
 * читается через `fetch`: кадры разделяет пустая строка, поля `event`, `data`, `id`; строка
 * с двоеточия — комментарий (сервер шлёт `: ping`). Обещание выполняется, когда сервер сам
 * закрыл соединение, и отклоняется при обрыве; `signal` обрывает чтение.
 */
export async function stream(
  path: string,
  options: { query?: Query; lastEventId?: string | null; signal: AbortSignal; onFrame: (frame: StreamFrame) => void },
): Promise<void> {
  const token = readToken();
  const headers: Record<string, string> = { accept: 'text/event-stream' };
  if (token) headers.authorization = `Bearer ${token}`;
  if (options.lastEventId) headers['last-event-id'] = options.lastEventId;

  const response = await fetch(`${BASE_URL}${withQuery(path, options.query)}`, { headers, signal: options.signal });
  if (!response.ok || !response.body) throw new ApiError(response.status, 'internal', `HTTP ${response.status}`);

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  let event = '';
  let data: string[] = [];
  let id: string | null = null;

  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += value;

    let newline = buffer.indexOf('\n');
    while (newline >= 0) {
      const line = buffer.slice(0, newline).replace(/\r$/, '');
      buffer = buffer.slice(newline + 1);
      newline = buffer.indexOf('\n');

      if (line === '') {
        if (event !== '') options.onFrame({ event, data: data.join('\n'), id });
        event = '';
        data = [];
        continue;
      }
      if (line.startsWith(':')) continue;

      const colon = line.indexOf(':');
      const field = colon < 0 ? line : line.slice(0, colon);
      const fieldValue = colon < 0 ? '' : line.slice(colon + 1).replace(/^ /, '');
      if (field === 'event') event = fieldValue;
      else if (field === 'data') data.push(fieldValue);
      else if (field === 'id') id = fieldValue;
    }
  }
}

/** Адрес веб-клиента: страницы условий и политики, ссылка «поделиться» товаром. */
export const WEB_BASE_URL: string = import.meta.env.VITE_WEB_URL ?? window.location.origin;
